import React, { useState, useEffect } from 'react';
import { Divider, Tag, Toggle } from 'rsuite';
import {
  FaClipboardList, FaUtensils, FaSlidersH, FaUserShield,
  FaMotorcycle, FaPercent, FaStar, FaBullhorn, FaImages,
  FaBuilding, FaChartBar, FaCalendarAlt, FaRobot, FaCheck,
} from 'react-icons/fa';
import RModal from '../../../components/Rsuite/Modal';
import { CustomButton } from '../../../components/CustomButton';
import { CustomInput } from '../../../components/CustomInput';
import { useCustomTheme } from '../../../hooks/useCustomTheme';
import api from '../../../services/api';
import { toastSuccess, toastError } from '../../../toasts';

const MODULOS = [
  { key: 'pedidos',       label: 'Pedidos',          icon: FaClipboardList },
  { key: 'cardapio',      label: 'Cardápio',         icon: FaUtensils      },
  { key: 'configuracoes', label: 'Configurações',    icon: FaSlidersH      },
  { key: 'usuarios',      label: 'Usuários',         icon: FaUserShield    },
  { key: 'entregadores',  label: 'Entregadores',     icon: FaMotorcycle    },
  { key: 'cupons',        label: 'Cupons',           icon: FaPercent       },
  { key: 'fidelidade',    label: 'Fidelidade',       icon: FaStar          },
  { key: 'promocoes',     label: 'Promoções',        icon: FaBullhorn      },
  { key: 'banners',       label: 'Banners',          icon: FaImages        },
  { key: 'empresa',       label: 'Dados da Empresa', icon: FaBuilding      },
  { key: 'relatorios',    label: 'Relatórios',       icon: FaChartBar      },
  { key: 'agendamento',   label: 'Agendamentos',     icon: FaCalendarAlt   },
  { key: 'chatbot',       label: 'Chatbot / Robô',   icon: FaRobot         },
];

const PermissaoCard = ({ modulo, selecionado, onClick, theme }) => {
  const Icon = modulo.icon;
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: '14px 8px',
        borderRadius: 10,
        border: `2px solid ${selecionado ? theme.colors.primary : theme.colors.backgroundSecondary}`,
        backgroundColor: selecionado ? `${theme.colors.primary}18` : theme.colors.backgroundSecondary,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        userSelect: 'none',
        position: 'relative',
        minHeight: 80,
      }}
    >
      {selecionado && (
        <div style={{
          position: 'absolute',
          top: 6,
          right: 6,
          width: 16,
          height: 16,
          borderRadius: '50%',
          backgroundColor: theme.colors.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <FaCheck size={8} color="#fff" />
        </div>
      )}
      <Icon
        size={20}
        color={selecionado ? theme.colors.primary : theme.colors.textSecondary}
        style={{ transition: 'color 0.15s ease' }}
      />
      <span style={{
        fontSize: 11,
        fontWeight: selecionado ? 600 : 400,
        color: selecionado ? theme.colors.primary : theme.colors.text,
        textAlign: 'center',
        lineHeight: 1.3,
        transition: 'all 0.15s ease',
      }}>
        {modulo.label}
      </span>
    </div>
  );
};

const EditarCadastrar = ({ open, onClose, grupo, idEmpresa, onSaved }) => {
  const { theme } = useCustomTheme();
  const [nome, setNome] = useState('');
  const [permissoes, setPermissoes] = useState([]);
  const [ativo, setAtivo] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (grupo) {
      setNome(grupo.Nome);
      setPermissoes(grupo.Permissoes || []);
      setAtivo(grupo.Ativo);
    } else {
      setNome('');
      setPermissoes([]);
      setAtivo(true);
    }
  }, [grupo]);

  const allSelected = permissoes.length === MODULOS.length;

  const togglePermissao = (key) => {
    setPermissoes((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  const handleSelectAll = () => {
    setPermissoes(allSelected ? [] : MODULOS.map((m) => m.key));
  };

  const handleSave = async () => {
    if (!nome.trim()) {
      toastError('Informe o nome do grupo');
      return;
    }
    setLoading(true);
    try {
      const payload = { Nome: nome.trim(), Permissoes: permissoes, Ativo: ativo, IdEmpresa: idEmpresa };
      if (grupo) {
        await api.patch(`grupo-acesso/alterar/${grupo.Id}`, payload);
        toastSuccess('Grupo atualizado com sucesso');
      } else {
        await api.post('grupo-acesso/criar', payload);
        toastSuccess('Grupo criado com sucesso');
      }
      onSaved();
    } catch (e) {
      toastError(e?.response?.data?.message || 'Erro ao salvar grupo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <RModal
      open={open}
      onClose={onClose}
      title={grupo ? 'Editar Grupo de Acesso' : 'Novo Grupo de Acesso'}
      size="md"
      footer={
        <>
          <CustomButton onClick={handleSave} loading={loading}>
            Salvar
          </CustomButton>
          <CustomButton appearance="ghost" onClick={onClose}>
            Fechar
          </CustomButton>
        </>
      }
    >
      <div style={{ padding: '4px 0 8px' }}>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'end', marginBottom: 20 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: theme.colors.textSecondary }}>
              Nome do Grupo
            </label>
            <CustomInput
              placeholder="Ex: Operador, Caixa, Atendente..."
              value={nome}
              onChange={setNome}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, paddingBottom: 2 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: theme.colors.textSecondary }}>Ativo</label>
            <Toggle
              checked={ativo}
              onChange={setAtivo}
              checkedChildren="Sim"
              unCheckedChildren="Não"
            />
          </div>
        </div>

        <Divider style={{ margin: '0 0 16px' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: theme.colors.textSecondary }}>
              Permissões
            </span>
            <Tag
              color={allSelected ? 'green' : permissoes.length > 0 ? 'blue' : 'default'}
              size="sm"
            >
              {permissoes.length} / {MODULOS.length}
            </Tag>
          </div>
          <span
            onClick={handleSelectAll}
            style={{
              fontSize: 12,
              cursor: 'pointer',
              color: theme.colors.primary,
              fontWeight: 500,
              textDecoration: 'underline',
            }}
          >
            {allSelected ? 'Desmarcar todos' : 'Selecionar todos'}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
          {MODULOS.map((m) => (
            <PermissaoCard
              key={m.key}
              modulo={m}
              selecionado={permissoes.includes(m.key)}
              onClick={() => togglePermissao(m.key)}
              theme={theme}
            />
          ))}
        </div>

      </div>
    </RModal>
  );
};

export default EditarCadastrar;
